import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './style.css';
//import InvoiceForm from '../InvoiceFormx';
import { Badge, Button, Card, CardBody, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Pagination, PaginationItem, PaginationLink, Popover, PopoverBody, Row, Spinner, UncontrolledDropdown } from 'reactstrap';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBoxOpen, FaCheckCircle, FaEdit, FaFileInvoice, FaFilePdf, FaFilter, FaInfo, FaMoneyBillWave, FaPlus, FaSearch, FaTrash, FaUpload, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { receiptNoteActions } from '../../sagas/receiptNoteSlice';
import InvoiceForm from './InvoiceForm';
import { formatPrice } from '../../helpers/utility';
import "jspdf-autotable";
import { productActions } from '../../sagas/productSlice';
import { generateReceptionLikePdfMake } from '../../helpers/generateReceptionDoc';
import { supplierActions } from '../../sagas/supplierSlice';
import Select from "react-select";
import { companyActions } from '../../sagas/companySlice';
import { useNavigate } from 'react-router-dom';
import useSessionState from '../../hooks/useSessionState';

const ReceptionPage = () => {
    const { t } = useTranslation();
    const [checkedReceptions, setCheckedReceptions] = useState([]);
    const [invoice, setInvoice] = useState(null); // State to hold the invoice object
    const [showInvoiceModal, setShowInvoiceModal] = useState(false); // State to control the invoice modal
    const { company, role } = useSelector(state => state.auth);
    const { companies } = useSelector((state) => state.companies);
    const defaultCompanyId = useMemo(
        () => companies?.find((item) => item.isDefault)?.id ?? null,
        [companies]
    );
    const { receiptNotes, isLoading } = useSelector((state) => state.receiptNote);
    const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
    const { loading } = useSelector((state) => state.invoiceSupplier);
    const isSuperAdmin = role === 'ROLE_SUPER_ADMIN';
    const [selectedSupplier, setSelectedSupplier] = useState('');
    const [currentPage, setCurrentPage] = useSessionState('receptionList.currentPage', 1);
    const [rowsPerPage, setRowsPerPage] = useSessionState('receptionList.rowsPerPage', 10);
    const [sortConfig, setSortConfig] = useSessionState('receptionList.sortConfig', { key: null, direction: 'asc' });
    const didRestoreListState = useRef(false);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const getMonthDateRange = (monthValue) => {
        if (!monthValue) {
            return { dateFrom: '', dateTo: '' };
        }

        const [year, month] = monthValue.split('-').map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);

        return {
            dateFrom: formatDate(firstDay),
            dateTo: formatDate(lastDay),
        };
    };
    const [filters, setFilters] = useSessionState('receptionList.filters', {
        companyId: null,
        fournisseurId: null,
        dateFrom: formatDate(monthStartDate),
        dateTo: formatDate(tomorrowDate),
        query: '',
        regle: -1,
        status: "",
        month: '',
    });

    const reglementOptions = [
        { value: -1, label: t('common.all', 'Tous') },
        { value: 0, label: t('reglement.nonRegle', 'Non réglé') },
        { value: 1, label: t('reglement.regle', 'Réglé') },
    ];

    const statusOptions = [
        { value: "", label: t('common.all', 'Tous') },
        { value: "EN_ATTENTE", label: 'En attente' },
        { value: "VALIDE", label: 'Confirmé' },
    ];


    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unite

    const searchInputRef = useRef(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        let payload = {
            ...filters,
        };
        dispatch(receiptNoteActions.search(payload));
    }, [filters.regle, filters.status, filters.companyId, filters.fournisseurId, filters.dateFrom, filters.dateTo]);

    useEffect(() => {
        if (suppliers.length === 0) {
            // Fetch suppliers for supplier report
            const fetchSuppliers = async () => {
                dispatch(supplierActions.search({
                    query: ""
                }));
            };

            fetchSuppliers();
        }
        dispatch(companyActions.getAll({}));
        dispatch(productActions.findAllUnites());
    }, []);

    useEffect(() => {
        if (defaultCompanyId && filters.companyId == null) {
            setFilters((prev) => ({ ...prev, companyId: defaultCompanyId }));
        }
    }, [defaultCompanyId]);

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [receiptNotes]);

    const handleSupplierChange = (value) => {
        setFilters(f => ({ ...f, fournisseurId: value }));
    };

    const fournisseurOptions = suppliers?.map(sup => ({
        solde: sup.solde,
        totalDebit: sup.totalDebit,
        totalCredit: sup.totalCredit,
        value: sup.id,
        label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom} (${sup.code})` : `${sup.entreprise}(${sup.code})`,
    })) ?? [];

    const companyOptions = companies?.map(item => ({
        value: item.id,
        label: item.name,
    })) ?? [];

    const companyOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...companyOptions
    ];

    const getReceiptCompanyName = (note) => {
        if (note?.companyName) return note.companyName;
        if (note?.company?.name) return note.company.name;
        return companies?.find(item => item.id === note?.companyId)?.name || '-';
    };

    const getReceiptCompany = (note) => {
        if (note?.company?.name) {
            return note.company;
        }

        return companies?.find(item => item.id === note?.companyId) || company;
    };

    const debouncedSearch = () => {
        dispatch(receiptNoteActions.search({
            ...filters,
        }));
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case 'EN_ATTENTE':
                return <Badge color="secondary">En attente confirmation</Badge>;
            case 'VALIDE':
                return <Badge color="success">Confirmée</Badge>;
            case 'ANNULE':
                return <Badge color="danger">Annulé</Badge>;

            default:
                return <Badge color="light">-</Badge>;
        }
    };

    const handleCheckboxChange = (noteId) => {
        setCheckedReceptions((prevChecked) => {
            if (prevChecked.includes(noteId)) {
                return prevChecked.filter((id) => id !== noteId);
            } else {
                return [...prevChecked, noteId];
            }
        });
    };

    const handleCreateInvoice = () => {
        if (checkedReceptions.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reception.noReceptionsSelected'),
            });
            return;
        }

        const selectedReceiptNotes = receiptNotes.filter(note =>
            checkedReceptions.includes(note.id)
        );

        // Validation: same supplier + confirmed
        const firstSupplier = selectedReceiptNotes[0].fournisseurId;
        const firstCompany = selectedReceiptNotes[0].companyId || selectedReceiptNotes[0].company?.id || null;
        const allSameSupplier = selectedReceiptNotes.every(n => n.fournisseurId === firstSupplier);
        const allSameCompany = selectedReceiptNotes.every(
            n => (n.companyId || n.company?.id || null) === firstCompany
        );
        const allConfirmed = selectedReceiptNotes.every(n => n.statut === 'VALIDE');

        if (!allSameSupplier) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reception.noteFromifferentSuppliers'),
            });
            return;
        }

        if (!allSameCompany) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('company.sameCompanyRequired', 'Veuillez sélectionner des réceptions de la même entreprise.'),
            });
            return;
        }

        if (!allConfirmed) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reception.notAllConfirmed'),
            });
            return;
        }

        const lineMap = {};

        selectedReceiptNotes.forEach(note => {
            note.lignes.forEach(line => {

                const tva = line.tauxTVA;
                const prixTTC = Number(line.prixUnitaireTTC || 0);

                // key combines article + price
                const key = `${line.articleId}-${prixTTC}`;

                if (!lineMap[key]) {
                    lineMap[key] = {
                        articleId: line.articleId,
                        articleNom: line.articleNom,
                        unite: unites.find(u => u.code === line.unite)?.libelle,
                        prixUnitaireHT: line.prixUnitaireHT,
                        prixUnitaireTTC: prixTTC,
                        tauxTVA: tva,
                        quantite: 0,
                        montantHT: 0,
                        montantTVA: 0,
                        montantTTC: 0
                    };
                }

                // increment the values
                lineMap[key].quantite += line.quantiteRecue;

                lineMap[key].montantHT += Number(line.montantHT || 0);
                lineMap[key].montantTTC += Number(line.montantTTC || 0);
                lineMap[key].montantTVA += Number((line.montantTVA) || 0);
            });
        });


        const aggregatedLines = Object.values(lineMap);

        // ------------------------------------------
        // Global Totals
        // ------------------------------------------
        const montantHT = selectedReceiptNotes.reduce((s, l) => s + l.montantTotalHT, 0);
        const montantTVA = selectedReceiptNotes.reduce((s, l) => s + l.montantTotalTaxe, 0);
        const montantTTC = selectedReceiptNotes.reduce((s, l) => s + l.montantTotalTTC, 0);

        // ------------------------------------------
        // Receipt -> Invoice mapping
        // ------------------------------------------
        const receiptMappings = selectedReceiptNotes.map(note => ({
            id: note.id,
            numero: note.numero,
            dateReception: note.dateReception,
            montantTTC: note.montantTotalTTC
        }));

        // ------------------------------------------
        // Build final invoice object
        // ------------------------------------------
        const newInvoice = {
            companyId: firstCompany,
            dateFacture: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
            dateOperation: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
            fournisseurId: selectedReceiptNotes[0].fournisseurId,
            fournisseurNom: selectedReceiptNotes[0].fournisseurNom,
            numeroFactfournisseur: '',
            statut: "IMPAYE",
            receiptMappings,
            lignes: aggregatedLines,
            montantRegle: selectedReceiptNotes.reduce((total, note) => total + (note.montantRegle || 0), 0),
            montantHT,
            montantTVA,
            montantTTC
        };
        setInvoice(newInvoice);
        setShowInvoiceModal(true);
    };

    const getReceiptRemainingAmount = (note) =>
        Math.max(Number(note?.montantTotalTTC || 0) - Number(note?.montantRegle || 0), 0);

    const isReceiptPaid = (note) =>
        note?.statusPaiement === 'PAYE' ||
        Boolean(note?.estRegle) || (
            Number(note?.montantTotalTTC || 0) > 0 &&
            getReceiptRemainingAmount(note) <= 0
        );

    const handleCreatePaymentFromReceptions = (receiptIds = checkedReceptions) => {
        const selectedReceiptNotes = receiptNotes.filter(note => receiptIds.includes(note.id));

        if (selectedReceiptNotes.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reception.noReceptionsSelected'),
            });
            return;
        }

        const firstSupplier = selectedReceiptNotes[0].fournisseurId;
        const firstCompany = selectedReceiptNotes[0].companyId || selectedReceiptNotes[0].company?.id || null;
        const allSameSupplier = selectedReceiptNotes.every(note => note.fournisseurId === firstSupplier);
        const allSameCompany = selectedReceiptNotes.every(note => (note.companyId || note.company?.id || null) === firstCompany);
        const allConfirmed = selectedReceiptNotes.every(note => note.statut === 'VALIDE');
        const payableDocuments = selectedReceiptNotes.filter(note => getReceiptRemainingAmount(note) > 0);

        if (!allSameSupplier) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reception.noteFromifferentSuppliers'),
            });
            return;
        }

        if (!allSameCompany) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('company.sameCompanyRequired', 'Veuillez selectionner des receptions de la meme entreprise.'),
            });
            return;
        }

        if (!allConfirmed) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning'),
                text: t('reception.notAllConfirmed'),
            });
            return;
        }

        if (payableDocuments.length === 0) {
            Swal.fire({
                icon: 'info',
                title: t('common.info', 'Information'),
                text: t('reglement.noAmountToPay', 'Aucun montant restant a regler.'),
            });
            return;
        }

        navigate('/purchases-module/payments/fournisseur/new', {
            state: {
                quickPayment: {
                    typeReglement: 'BON_RECEPTION',
                    fournisseurId: firstSupplier,
                    companyId: firstCompany,
                    montantRegle: payableDocuments.reduce((total, note) => total + getReceiptRemainingAmount(note), 0),
                    documents: payableDocuments,
                },
            },
        });
    };


    const handleCloseInvoiceModal = () => {
        setShowInvoiceModal(false);
        setInvoice(null);
        setCheckedReceptions([]); // Clear checked receptions after closing the invoice modal
        let payload = {
            ...filters
        };
        dispatch(receiptNoteActions.search(payload));

    };
    const handleShowModal = () => {
        navigate('/purchases-module/reception/new');
    };

    const handleValidate = (receiptNote) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reception.confirmValidateMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('reception.confirmValidate'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                if (receiptNote.id > 0) {
                    dispatch(receiptNoteActions.validateReceiptNote({
                        id: receiptNote.id,
                        onSuccess: () => {
                            Swal.fire(
                                t('common.saved'),
                                t('reception.savedSuccess'),
                                'success'
                            );
                            refreshReceiptNotes();
                        },
                        onError: (error) => {
                            Swal.fire(
                                t('common.error'),
                                t('reception.saveError'),
                                'error'
                            );
                        }
                    }));
                }
            }
        });
    };

    const refreshReceiptNotes = () => {
        let payload = {
            ...filters
        };
        dispatch(receiptNoteActions.search(payload));
    };

    const clearFilters = () => {
        setFilters({
            companyId: defaultCompanyId,
            query: '',
            fournisseurId: null,
            dateFrom: formatDate(monthStartDate),
            dateTo: formatDate(tomorrowDate),
            regle: -1,
            status: "",
            month: '',
        });
    };

    const sortedReceiptNotes = useMemo(() => {
        const rows = [...(receiptNotes || [])];
        if (!sortConfig.key) return rows;

        rows.sort((a, b) => {
            const left = sortConfig.key === 'dateReception'
                ? (a.dateReception ? new Date(a.dateReception).getTime() || 0 : 0)
                : Number(a.montantTotalTTC || 0);
            const right = sortConfig.key === 'dateReception'
                ? (b.dateReception ? new Date(b.dateReception).getTime() || 0 : 0)
                : Number(b.montantTotalTTC || 0);

            return sortConfig.direction === 'asc' ? left - right : right - left;
        });

        return rows;
    }, [receiptNotes, sortConfig]);

    const totalPages = Math.max(1, Math.ceil(sortedReceiptNotes.length / rowsPerPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const paginatedReceiptNotes = sortedReceiptNotes.slice((currentPageSafe - 1) * rowsPerPage, currentPageSafe * rowsPerPage);
    const pageStart = sortedReceiptNotes.length === 0 ? 0 : (currentPageSafe - 1) * rowsPerPage + 1;
    const pageEnd = Math.min(currentPageSafe * rowsPerPage, sortedReceiptNotes.length);

    useEffect(() => {
        if (!didRestoreListState.current) {
            didRestoreListState.current = true;
            return;
        }
        setCurrentPage(1);
    }, [filters.regle, filters.status, filters.companyId, filters.fournisseurId, filters.dateFrom, filters.dateTo, filters.query, filters.month, rowsPerPage]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const renderSortableHeader = (label, key) => (
        <th
            className="align-middle text-center"
            onClick={() => handleSort(key)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
        >
            {label}{sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
        </th>
    );

    if (isLoading || loading) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                }}
            >
                <Spinner color="primary" />
            </div>
        );
    }

    const handleAnnulerBR = (br) => {
        Swal.fire({
            title: t('reception.deleteConfirmTitle'),
            text: t('reception.deleteConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {

                    let payload = {
                        id: br.id,
                        onSuccess: () => {
                            Swal.fire(t('reception.deleted'), '', 'success');
                            refreshReceiptNotes();
                        },
                        onError: (error) => {
                            Swal.fire({
                                icon: 'error',
                                title: t('reception.deleteError'),
                                text: t('reception.deleteErrorMessage')
                            });
                        }
                    }
                    dispatch(
                        isSuperAdmin
                            ? receiptNoteActions.annulerBRAdmin(payload)
                            : receiptNoteActions.annulerBR(payload)
                    );

                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: t('common.error'),
                        text: t('reception.deleteError')
                    });
                }
            }
        });
    }



    return (
        <Container fluid className="enterprise-page document-enterprise-page">
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaBoxOpen className="text-primary me-2" />
                                {t('reception.title')}
                            </h4>
                            <div className="d-flex align-items-center gap-2">
                                <div style={{ minWidth: '260px' }}>
                                    <Select
                                        id="companyIdHeader"
                                        options={companyOptionsWithAll}
                                        value={companyOptionsWithAll.find(option => option.value === filters.companyId) || null}
                                        onChange={(selectedOption) => setFilters(f => ({ ...f, companyId: selectedOption ? selectedOption.value : null }))}
                                        placeholder={t('company.selectCompany', 'Filtrer par entreprise')}
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                {/* Primary actions */}
                                <Button color="primary" onClick={handleShowModal}>
                                    <FaPlus className="me-2" /> {t('reception.newReception')}
                                </Button>
                                {checkedReceptions.length > 0 && (
                                    <Button color="success" onClick={handleCreateInvoice}>
                                        <FaFileInvoice className="me-2" />
                                        {t('reception.createInvoice')}
                                    </Button>
                                )}
                                {checkedReceptions.length > 0 && (
                                    <Button color="warning" onClick={() => handleCreatePaymentFromReceptions()}>
                                        <FaMoneyBillWave className="me-2" />
                                        {t('reglement.pay', 'Regler')}
                                    </Button>
                                )}

                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? ( // Show spinner when loading
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <Row className="mb-3 g-2 align-items-end">
                                    <Col lg={2} md={2} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('reglement.search')}</Label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <FaSearch />
                                                </span>
                                                <Input
                                                    type="text"
                                                    innerRef={searchInputRef}
                                                    placeholder={t('reception.searchPlaceholder')}
                                                    value={filters.query}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            query: value
                                                        }));
                                                    }}
                                                    className="text-uppercase"
                                                    style={{ textTransform: 'uppercase' }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            debouncedSearch(e.target.value.toUpperCase());
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={2}>
                                        <FormGroup>
                                            <Label for="supplierSelect" className="form-label">
                                                <FaUser className="me-1" /> Fournisseur
                                            </Label>
                                            <Select
                                                id="supplierSelect"
                                                options={fournisseurOptions}
                                                isLoading={suppliersLoading}
                                                value={fournisseurOptions.find(option => option.value === filters.fournisseurId)}
                                                onChange={(option) => handleSupplierChange(option?.value)}
                                                placeholder="Sélectionner..."
                                                isClearable
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={2} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('common.month', 'Mois')}</Label>
                                            <Input
                                                type="month"
                                                value={filters.month}
                                                onChange={e => {
                                                    const month = e.target.value;
                                                    const { dateFrom, dateTo } = getMonthDateRange(month);
                                                    setFilters(f => ({
                                                        ...f,
                                                        month,
                                                        dateFrom: dateFrom || f.dateFrom,
                                                        dateTo: dateTo || f.dateTo,
                                                    }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateFrom" className="mb-1">Date début</Label>
                                            <Input
                                                type="date"
                                                id="dateFrom"
                                                value={filters.dateFrom}
                                                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, month: '' }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateTo" className="mb-1">Date fin</Label>
                                            <Input
                                                type="date"
                                                id="dateTo"
                                                value={filters.dateTo}
                                                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, month: '' }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col xl={2} lg={2} md={6}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('reglement.status', 'Paiement')}</Label>
                                            <Input
                                                type="select"
                                                value={filters.regle}
                                                onChange={e =>
                                                    setFilters(f => ({ ...f, regle: e.target.value }))
                                                }
                                            >
                                                {reglementOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col xl={1} lg={2} md={6}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">Status</Label>
                                            <Input
                                                type="select"
                                                value={filters.status}
                                                onChange={e =>
                                                    setFilters(f => ({ ...f, status: e.target.value }))
                                                }
                                            >
                                                {statusOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={clearFilters}
                                            title={t('common.clearFilters')}
                                            className="mb-3"
                                            style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FaFilter />
                                        </Button>
                                    </Col>
                                    <Col lg={1} md={2} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">Lignes</Label>
                                            <Input
                                                type="select"
                                                value={rowsPerPage}
                                                onChange={e => setRowsPerPage(Number(e.target.value))}
                                            >
                                                {[10, 20, 50, 100].map((size) => (
                                                    <option key={size} value={size}>{size}</option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>

                            <div className="enterprise-table-scroll">
                            <table className="table table-hover table-striped table-bordered document-grouped-table" style={{ fontSize: '0.8rem' }}>
                                <thead className="thead-light">
                                    <tr>
                                        <th className="align-middle text-center" width="20">
                                            <div className="form-check">
                                                <Input
                                                    type="checkbox"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            // Only select deliveries that are in LIVRE status
                                                            const recpIds = receiptNotes
                                                                .filter(br => br.statut === 'VALIDE' && !br.facture)
                                                                .map(br => br.id);
                                                            setCheckedReceptions(recpIds);
                                                        } else {
                                                            setCheckedReceptions([]);
                                                        }
                                                    }}
                                                    checked={
                                                        checkedReceptions.length > 0 &&
                                                        checkedReceptions.length === receiptNotes.filter(br => br.statut === 'VALIDE' && !br.facture).length
                                                    }
                                                />
                                            </div>
                                        </th>
                                        <th className="align-middle text-center">
                                            <div>{t('reception.receiptNumber')}</div>
                                            <div
                                                className="small text-muted mt-1"
                                                onClick={() => handleSort('dateReception')}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                {t('reception.receiptDate')}
                                                {sortConfig.key === 'dateReception' && (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
                                            </div>
                                        </th>
                                        <th className="align-middle">{t('reception.supplier')}</th>
                                        {renderSortableHeader(
                                            `${t('facture.table.amounts', 'Montants')} TTC`,
                                            'montantTotalTTC'
                                        )}
                                        <th className="align-middle text-center">{t('reception.status')}</th>
                                        <th className="align-middle text-center">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedReceiptNotes.map((note) => {
                                        const remainingAmount = getReceiptRemainingAmount(note);
                                        const isPaid = isReceiptPaid(note);
                                        const isUnpaid = note.statut === 'VALIDE' && !isPaid;
                                        return (
                                            <tr key={note.id} className={isPaid ? 'table-success' : isUnpaid ? 'table-danger' : ''}>
                                            <td className="align-middle text-center">
                                                {note.statut === 'VALIDE' && !note.facture && (
                                                    <Input
                                                        type="checkbox"
                                                        checked={checkedReceptions.includes(note.id)}
                                                        onChange={() => handleCheckboxChange(note.id)}
                                                    />
                                                )}
                                            </td>
                                            <td className="align-middle">
                                                <div className="document-main-ref">{note.numero}</div>
                                                {isPaid && (
                                                    <Badge color="success" pill className="mt-1">
                                                        {t('reglement.regle', 'Réglé')}
                                                    </Badge>
                                                )}
                                                {isUnpaid && (
                                                    <Badge color="danger" pill className="mt-1">
                                                        {t('reglement.nonRegle', 'Non réglé')}
                                                    </Badge>
                                                )}
                                                <div className="document-subtext">{t('reception.receiptDate')}: {note?.dateReception || 'N/A'}</div>
                                                <div className="document-subtext">{t('reception.purchaseOrderNumber')}: {note.commandeNumero || '-'}</div>
                                                <div className="document-subtext">{t('reception.deliveryNoteNb')}: {note.deliveryNoteNb || '-'}</div>
                                                <div className="document-subtext">{t('common.user')}: {note.createdBy || 'N/A'}</div>
                                                {note.commentaire && (
                                                    <div className="document-subtext">
                                                        {t('common.commentaire', 'Commentaire')}: {note.commentaire}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="align-middle">
                                                <div className="document-strong">{note.fournisseurNom || 'N/A'} {note.fournisseurCode ? `(${note.fournisseurCode})` : ''}</div>
                                                <div className="document-subtext">{t('company.company', 'Entreprise')}: {getReceiptCompanyName(note) || '-'}</div>
                                                <div className="document-subtext">{t('reception.entrepot')}: {note.entrepotNom || '-'}</div>
                                            </td>
                                            <td className="align-middle">
                                                <div className="document-amount-grid">
                                                    <span>TTC</span><strong>{formatPrice(note.montantTotalTTC, false)}</strong>
                                                    <span>{t('reglement.montantPaye')}</span><span>{formatPrice(note.montantRegle, false)}</span>
                                                    <span>{t('reglement.resteAPayer', 'Reste')}</span><strong>{formatPrice(remainingAmount, false)}</strong>
                                                </div>
                                            </td>
                                            <td className="align-middle text-center">
                                                {getStatusBadge(note.statut)}
                                                <div className="document-subtext mt-1">
                                                    {t('delivery.regle')}:{' '}
                                                    {isPaid ? (
                                                        <span className="badge bg-success">{t('reglement.regle', 'Réglé')}</span>
                                                    ) : (
                                                        <span className="badge bg-danger">{t('reglement.nonRegle', 'Non réglé')}</span>
                                                    )}
                                                </div>
                                                <div className="document-subtext">
                                                    {t('delivery.facture')}: {note.facture ? t('common.yes', 'Oui') : t('common.no', 'Non')}
                                                </div>
                                            </td>
                                            <td className="align-middle text-center">
                                                <div className="btn-group">
                                                    {note.statut === 'EN_ATTENTE' && <Button
                                                        color="success"
                                                        title={t('common.confirm')}
                                                        aria-label={t('common.confirm')}
                                                        onClick={() => handleValidate(note)}
                                                        className='me-1'
                                                        size="sm"
                                                    >
                                                        <FaCheckCircle />
                                                    </Button>}

                                                    <UncontrolledDropdown>
                                                        <DropdownToggle caret color="secondary" size="sm" className="me-1">
                                                            <FaFilePdf />
                                                        </DropdownToggle>

                                                        <DropdownMenu>
                                                            {/* PDF WITH PRICES */}
                                                            <DropdownItem
                                                                onClick={() =>
                                                                    generateReceptionLikePdfMake(note, {
                                                                        docType: "BON DE RÉCEPTION",
                                                                        showPrices: true,
                                                                        priceMode: "TTC",
                                                                        company: getReceiptCompany(note),
                                                                    })
                                                                }
                                                            >
                                                                {t('reception.pdfWithPrices')}
                                                            </DropdownItem>

                                                            {/* PDF WITHOUT PRICES */}
                                                            <DropdownItem
                                                                onClick={() =>
                                                                    generateReceptionLikePdfMake(note, {
                                                                        docType: "BON DE RÉCEPTION",
                                                                        showPrices: false,
                                                                        company: getReceiptCompany(note),
                                                                    })
                                                                }
                                                            >
                                                                {t('reception.pdfWithoutPrices')}
                                                            </DropdownItem>
                                                        </DropdownMenu>
                                                    </UncontrolledDropdown>


                                                    <Button color="warning" title='Consulter' onClick={() => {
                                                        navigate('/purchases-module/reception/edit', { state: { reception: note } });
                                                    }} size="sm" className="me-1">
                                                        <FaEdit size={12} className="mr-2" />
                                                    </Button>
                                                    {note.statut === 'VALIDE' && !note.facture && remainingAmount > 0 && (
                                                        <Button
                                                            color="success"
                                                            title={t('reglement.pay', 'Regler')}
                                                            onClick={() => handleCreatePaymentFromReceptions([note.id])}
                                                            size="sm"
                                                            className="me-1"
                                                        >
                                                            <FaMoneyBillWave size={12} />
                                                        </Button>
                                                    )}
                                                    {(note.statut === 'EN_ATTENTE' || (isSuperAdmin && note.statut === 'VALIDE')) && <Button color="danger" size="sm" className="px-2 py-1">
                                                        <FaTrash size={12} onClick={() => handleAnnulerBR(note)} />
                                                    </Button>}
                                                </div>
                                            </td>
                                            </tr>
                                        );
                                    })}
                                    {receiptNotes.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                <div className="text-muted">{t('reception.noRC')}</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light fw-bold">
                                        <td colSpan="3" className="text-end">{t('common.totals')}</td>
                                        <td className="align-middle">
                                            <div className="document-amount-grid">
                                                <span>TTC</span><strong>{formatPrice(receiptNotes.reduce((sum, bl) => sum + (bl.montantTotalTTC || 0), 0), false)}</strong>
                                                <span>{t('reglement.montantPaye')}</span><span>{formatPrice(receiptNotes.reduce((sum, bl) => sum + (bl.montantRegle || 0), 0), false)}</span>
                                            </div>
                                        </td>
                                        <td colSpan="2"></td>
                                    </tr>
                                </tfoot>

                            </table>
                            </div>
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
                                <div className="text-muted small">
                                    {t('common.paginationInfo', {
                                        defaultValue: '{{start}}-{{end}} sur {{total}}',
                                        start: pageStart,
                                        end: pageEnd,
                                        total: receiptNotes?.length || 0,
                                    })}
                                </div>
                                <Pagination size="sm" className="mb-0">
                                    <PaginationItem disabled={currentPageSafe === 1}>
                                        <PaginationLink first onClick={() => handlePageChange(1)}>
                                            <FaAngleDoubleLeft />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === 1}>
                                        <PaginationLink previous onClick={() => handlePageChange(currentPageSafe - 1)}>
                                            <FaAngleLeft />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled>
                                        <PaginationLink>{currentPageSafe} / {totalPages}</PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === totalPages}>
                                        <PaginationLink next onClick={() => handlePageChange(currentPageSafe + 1)}>
                                            <FaAngleRight />
                                        </PaginationLink>
                                    </PaginationItem>
                                    <PaginationItem disabled={currentPageSafe === totalPages}>
                                        <PaginationLink last onClick={() => handlePageChange(totalPages)}>
                                            <FaAngleDoubleRight />
                                        </PaginationLink>
                                    </PaginationItem>
                                </Pagination>
                            </div>
                        </>)}
                </CardBody>
            </Card>
            <Modal isOpen={showInvoiceModal} toggle={handleCloseInvoiceModal} size="xl">
                <ModalHeader toggle={handleCloseInvoiceModal}>
                    {t('facture.newInvoice')}
                </ModalHeader>
                <ModalBody>
                    {invoice && (
                        <InvoiceForm
                            initialValues={invoice}
                            onClose={handleCloseInvoiceModal}
                        />
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
}

export default ReceptionPage;
